"use client";
import { useEffect, useRef, useState } from "react";

/**
 * @name useScrollable
 * @description
 * Custom hook to manage horizontal scrolling logic, including scroll position detection and navigation controls.
 * It provides a ref to attach to the scrollable container and boolean flags indicating if scrolling is possible in either direction.
 * It also exposes a scroll function to programmatically scroll the container.
 *
 * Uses `ResizeObserver` to detect size changes of the scrollable element (e.g., due to sidebar toggles or window resizing)
 * and simple event listeners for scroll updates.
 *
 * @example
 * ```tsx
 * const { scrollRef, canScrollLeft, canScrollRight, scroll } = useScrollable();
 *
 * return (
 *   <div>
 *     {canScrollLeft && <button onClick={() => scroll(-200)}>Left</button>}
 *     <div ref={scrollRef}>...items</div>
 *     {canScrollRight && <button onClick={() => scroll(200)}>Right</button>}
 *   </div>
 * )
 * ```
 *
 * @returns {Object} An object containing the scroll ref, scroll state flags, and scroll control function.
 * @property {React.RefObject<HTMLDivElement>} scrollRef - Ref to be attached to the scrollable container.
 * @property {boolean} canScrollLeft - Boolean indicating if the container can be scrolled to the left.
 * @property {boolean} canScrollRight - Boolean indicating if the container can be scrolled to the right.
 * @property {(amount?: number) => void} scroll - Function to scroll the container by a specified amount (positive for right, negative for left).
 */
export const useScrollable = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const scroll = (amount = 200) => {
    scrollRef.current?.scrollBy({ left: amount, behavior: "smooth" });
  };

  useEffect(() => {
    const scrollableElement = scrollRef.current;
    if (!scrollableElement) return;

    const checkScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = scrollableElement;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
    };

    checkScroll();
    const observer = new ResizeObserver(checkScroll);
    observer.observe(scrollableElement);
    scrollableElement.addEventListener("scroll", checkScroll);

    return () => {
      observer.disconnect();
      scrollableElement.removeEventListener("scroll", checkScroll);
    };
  }, []);

  return { scrollRef, canScrollLeft, canScrollRight, scroll };
};
